import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as List from 'react-window';
import { LoadingStatusWrapper } from 'sharedComponents';
import { apiCallStatus } from 'helpers/constants';
import './_style.scss';
import { listTypes, defaultRowHeight } from './constants';

const ScrollableListViewComponent = ({
    data,
    rowHeight,
    height,
    width,
    renderRow,
    onLoadMore,
    hasMore,
    noWrapper,
    loadingStatus,
    emptyLabel,
    withLoading,
    listType,
    className = '',
    initialScrollIndex
}) => {
    const listRef = useRef();
    const [rowSizes, setRowSizes] = useState({});
    const [previousIndex, setPreviousIndex] = useState(null);
    const hasMountedRef = useRef(false);

    useEffect(() => {
        return () => {
            setRowSizes({});
            setPreviousIndex(null);
        };
    }, []);

    // Scroll to initial index on mount
    useEffect(() => {
        if (!hasMountedRef.current && initialScrollIndex !== undefined && initialScrollIndex >= 0 && data?.length > 0 && listRef.current) {
            // Calculate scroll position to center the item
            const itemTop = listType === listTypes.VariableSizeList || listType === listTypes.VariableSizeGrid
                ? rowSizes[initialScrollIndex]?.top ?? initialScrollIndex * rowHeight
                : initialScrollIndex * rowHeight;
            const scrollOffset = itemTop - height / 2 + rowHeight / 2;

            listRef.current.scrollTo(Math.max(0, scrollOffset));
            hasMountedRef.current = true;
        }
    }, [initialScrollIndex, data?.length, rowSizes, height, rowHeight, listType]);

    useEffect(() => {
        if (data?.length > 0 && listType !== listTypes.FixedSizeList && listType !== listTypes.FixedSizeGrid) {
            setRowSizes((prevRowSizes) => {
                const updatedRowSizes = { ...prevRowSizes };

                // Append new rows with default height and top
                for (let i = Object.keys(updatedRowSizes).length; i < data.length; i++) {
                    const cumulativeHeight = Object.values(updatedRowSizes).reduce((acc, row) => acc + row.height, 0);
                    updatedRowSizes[i] = { height: rowHeight, top: cumulativeHeight };
                }

                return updatedRowSizes;
            });
        }
    }, [data?.length, rowHeight]);

    // Toggle row size dynamically
    const toggleSize = useCallback((index, count) => {
        if (listRef.current) {
            listRef.current.resetAfterIndex(0);
        }
        setRowSizes((prevRowSizes) => {
            const updatedRowSizes = { ...prevRowSizes };
            updatedRowSizes[index].height =
                prevRowSizes[index].height === rowHeight ? rowHeight * count : rowHeight;
            if (previousIndex !== null && previousIndex !== index) {
                updatedRowSizes[previousIndex].height = rowHeight; // Reset height for the previous index
            }
            // Update top styles for all rows
            let cumulativeHeight = 0;
            Object.keys(updatedRowSizes).forEach((key) => {
                updatedRowSizes[key].top = cumulativeHeight;
                cumulativeHeight += updatedRowSizes[key].height;
            });

            return updatedRowSizes;
        });
        setPreviousIndex(index);
    }, [rowHeight, previousIndex]);

    // Get size for each row
    const getSize = useCallback((index) => rowSizes[index]?.height || rowHeight, [rowSizes, rowHeight]);

    // Lazy load more items when scrolled to end
    const handleScroll = useCallback(
        ({ scrollOffset, scrollDirection, scrollUpdateWasRequested }) => {
            if (
                scrollDirection === 'forward' &&
                !scrollUpdateWasRequested &&
                onLoadMore &&
                hasMore
            ) {
                const bottomOffset = scrollOffset + height;
                // For FixedSizeList/Grid rowSizes is never populated, so derive
                // totalHeight from item count instead of the always-empty map.
                const totalHeight =
                    listType === listTypes.FixedSizeList || listType === listTypes.FixedSizeGrid
                        ? (data?.length || 0) * rowHeight
                        : Object.values(rowSizes).reduce((acc, row) => acc + row.height, 0);
                // Trigger load more when within 1 row of the end
                if (totalHeight > 0 && bottomOffset >= totalHeight - rowHeight) {
                    onLoadMore();
                }
            }
        },
        [rowSizes, rowHeight, height, onLoadMore, hasMore, listType, data?.length]
    );

    // Memoize children rendering
    const children = useMemo(() => {
        const ListComponent = List[listType];
        if (data?.length > 0) {
            const itemSize =
                listType === listTypes.FixedSizeList || listType === listTypes.FixedSizeGrid
                    ? rowHeight
                    : getSize;
            return (
                <ListComponent
                    ref={listRef}
                    height={height}
                    className='virtualized-list'
                    itemCount={data?.length}
                    itemSize={itemSize}
                    width={width}
                    onScroll={handleScroll}
                    overscanCount={10} // Number of items to render outside of the visible area
                >
                    {({ index, style }) =>
                        <div
                            className='list-item-pt'
                            style={{
                                ...style,
                                height: rowSizes[index]?.height || rowHeight,
                                top: rowSizes[index]?.top || style.top,
                            }}
                            id={index}
                        >
                            {renderRow({ item: data[index], index, style, toggleSize })}
                        </div>}
                </ListComponent>
            );
        }
        if (loadingStatus !== apiCallStatus.LOADING) {
            return <div className='scrollable-list_empty-label'>{emptyLabel}</div>;
        }
        return null;
    }, [data, height, rowHeight, width, handleScroll, loadingStatus, emptyLabel, toggleSize, getSize, rowSizes]);

    // Memoize content rendering
    const content = useMemo(() => {
        if (withLoading) {
            return (
                <LoadingStatusWrapper transparent useCustomAnchor loadingStatus={loadingStatus}>
                    {noWrapper ? children : <div className='scrollable-list-with-loading'>{children}</div>}
                </LoadingStatusWrapper>
            );
        }
        return children;
    }, [withLoading, loadingStatus, noWrapper, children]);

    if (noWrapper) {
        return content;
    }

    return (
        <div
            className={`scrollable-list ${className} ${loadingStatus === apiCallStatus.LOADING ? 'loading' : ''}`}
            role='scrollable-list'
        >
            {content}
        </div>
    );
};

ScrollableListViewComponent.propTypes = {
    data: PropTypes.array.isRequired,
    rowHeight: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    renderRow: PropTypes.func.isRequired,
    onLoadMore: PropTypes.func,
    hasMore: PropTypes.bool,
    className: PropTypes.string,
    noWrapper: PropTypes.bool,
    emptyLabel: PropTypes.string,
    withLoading: PropTypes.bool,
    loadingStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    listType: PropTypes.oneOf(['FixedSizeList', 'VariableSizeList', 'FixedSizeGrid', 'VariableSizeGrid']),
    initialScrollIndex: PropTypes.number,
};

ScrollableListViewComponent.defaultProps = {
    onLoadMore: null,
    hasMore: false,
    className: '',
    noWrapper: false,
    emptyLabel: '',
    withLoading: false,
    loadingStatus: apiCallStatus.IDLE,
    listType: 'FixedSizeList',
    rowHeight: defaultRowHeight,
    initialScrollIndex: undefined,
};

export default React.memo(ScrollableListViewComponent);
